import { DestroyRef, Injectable, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import {
  CONTAINER_TERMINAL_STEP,
  DIAGNOSTIC_TOAST_LIFE_MS,
  LOAD_FAILURE_COPY,
  LOAD_WATCHDOG_MS,
  LoadFailureClass,
  SILENT_FAILURE_CLASSES,
  SHEET_TERMINAL_STEP,
  formatLoadDiagnosticCode,
  formatToastDiagnosticCode,
  formatStepsReachedForConsole,
} from '../../utils/sheet-load-trace.util';
import { AuthFailureNoticeService } from '../auth/auth-failure-notice.service';
import { RuntimeConfigService } from '../common/runtime-config.service';
import { legacySheetLoadFailureMessage } from '../../utils/legacy-sheet-load-failure-copy.util';

/** Which half of the load a trace belongs to (console-only in the code itself). */
export type LoadFailureOrigin = 'entry' | 'conversion' | 'sheet';

/**
 * A failure ready to be shown, emitted on {@link SheetLoadTraceService.failures$}.
 *
 * The service composes the copy but does NOT render it — see that stream's
 * docblock for why the toast has to be raised somewhere else.
 */
export interface LoadFailureNotice {
  /** The class this notice reports. A page that reacts to a failure keys on it, never on `summary`. */
  failure: LoadFailureClass;
  /** `S<role>` of the sheet involved (the scope the CONSOLE code prints, not the toast's). */
  scope: string;
  /** Which load raised it. Only `'sheet'` notices can put a failure panel over a sheet. */
  origin: LoadFailureOrigin;
  /**
   * `true` when the load ended (`fail()`), `false` for a degraded report
   * (`warn()`) after which the load carries on. A degraded notice is a toast only.
   */
  terminal: boolean;
  /**
   * The diagnostic code as the USER sees it, e.g. `MBRS-S13-ASSET-a1b2c3d4` —
   * or just `MBRS-PERM` for an identifier-free class (see
   * `IDENTIFIER_FREE_FAILURE_CLASSES`). The console keeps the full code.
   */
  code: string;
  /**
   * The single toast line: the preparer's sentence followed by the code, e.g.
   * "This sheet's form layout couldn't be downloaded. … MBRS-S13-ASSET-01M45NK5" (P.O.,
   * 2026-10-05 — no "Sheet didn't load"/"Filing data didn't load" prefix and
   * no separate detail line underneath it).
   */
  summary: string;
  /** How long the toast should stay up. */
  life: number;
}

/**
 * Compose the toast for a classified failure — the ONE place the
 * `refactoredDiagnosticCodesEnabled` switch decides between the coded sentence
 * and the previous generic one, shared by a load trace and the pre-navigation
 * filing guard so the two can never drift.
 *
 * Single line (P.O., 2026-10-05): the preparer's sentence followed by the code,
 * e.g. "This sheet's form layout couldn't be downloaded. … MBRS-S13-ASSET-01M45NK5".
 * A PERM line reads "You don't have access to this record. MBRS-PERM"
 * (P.O., 2026-10-12). With the flag off (production) the user reads the
 * previous generic sentence and no code.
 *
 * @param failure       Never `AUTH` — that class is silent and has no legacy copy.
 * @param toastCode     The code as the USER sees it (`formatToastDiagnosticCode`).
 * @param codesEnabled  `RuntimeConfigService.refactoredDiagnosticCodesEnabled()`.
 */
function composeLoadFailureNotice(
  failure: LoadFailureClass,
  toastCode: string,
  codesEnabled: boolean,
  context: { scope: string; origin: LoadFailureOrigin; terminal: boolean },
): LoadFailureNotice {
  // REFACTORED-DIAGNOSTIC-CODES-FLAG: remove the legacy branch with the flag.
  const legacyMessage = codesEnabled ? null : legacySheetLoadFailureMessage(failure);
  return {
    failure,
    scope: context.scope,
    origin: context.origin,
    terminal: context.terminal,
    code: legacyMessage === null ? toastCode : '',
    summary: legacyMessage ?? `${LOAD_FAILURE_COPY[failure]} ${toastCode}`,
    life: DIAGNOSTIC_TOAST_LIFE_MS,
  };
}

/**
 * A single in-flight load, and the only thing a caller interacts with.
 *
 * Obtained from {@link SheetLoadTraceService.beginContainerLoad} or
 * {@link SheetLoadTraceService.beginSheetLoad}, then driven with `mark()` as
 * each step completes and closed with exactly one of `succeed()` or `fail()`.
 */
export interface LoadTrace {
  /**
   * Record that a step COMPLETED. Call this immediately after the work the step
   * names, never before it and never in a `catch` — the whole diagnostic value
   * of the console step ledger comes from the gap left by a step that never
   * got here.
   */
  mark(step: number): void;

  /**
   * Close the trace as failed: cancels the watchdog, logs the full detail to the
   * console, and shows the toast carrying the diagnostic code.
   *
   * Safe to call more than once and safe to call after `succeed()` — only the
   * first close wins, so a failure that cascades through nested catches shows
   * one toast rather than three.
   *
   * @param failure The classification. See `LoadFailureClass`.
   * @param cause   The underlying error or status text, for the console only.
   *                It is never shown to the user, per the rule that UI copy
   *                keys on a code and never on server message prose.
   */
  fail(failure: LoadFailureClass, cause?: unknown): void;

  /**
   * Report a DEGRADED load — something non-essential was unavailable and the load
   * carries on without it (today: role 2's MSIC code list, class `EMPTY`).
   *
   * Unlike {@link fail} this does NOT close the trace, cancel the watchdog or set
   * {@link failureSummary}: it logs the full code to the console and emits a toast
   * notice (`terminal: false`), nothing more. That is the whole reason it exists —
   * `fail()` is "first close wins", so reporting a degraded asset through it would
   * swallow a genuine failure later in the same load (a `BUILD` after the MSIC
   * list failed would show no toast and no code) and would cancel the stall clock.
   * A no-op on a trace that is already closed or abandoned.
   */
  warn(failure: LoadFailureClass, cause?: unknown): void;

  /** Close the trace as successful: cancels the watchdog and frees the slot. */
  succeed(): void;

  /**
   * Close the trace WITHOUT reporting — the load was neither completed nor
   * broken, it was abandoned. Wired automatically to the caller's `DestroyRef`,
   * so a sheet that unmounts mid-load cannot report on itself afterwards.
   */
  abandon(): void;

  /** The steps recorded so far. Exposed for specs and for the console record. */
  readonly stepsReached: readonly number[];

  /**
   * The single-line summary `fail()` emitted on `failures$` (sentence plus
   * diagnostic code), or null if this trace has not reported a failure — it
   * succeeded, was abandoned, is still open, or failed with a silent class.
   * Read by `FilingFormContainerComponent` so its load-failure panel shows
   * exactly what the toast showed, even when the toast gate suppresses a
   * repeat toast.
   */
  readonly failureSummary: string | null;
}

/**
 * A trace that records nothing and reports nothing.
 *
 * Every sheet's `loadTraceHandle` is initialised to this, so the field is never
 * undefined. That matters because `loadSchema()` is not reached only through
 * `ngOnInit`: the cancel/revert paths re-run parts of init, and specs call it
 * directly to exercise schema selection. Those callers have no live trace, and
 * before this they hit `Cannot read properties of undefined (reading 'mark')` —
 * a diagnostic aid that crashes the thing it is diagnosing is worse than none.
 *
 * A null object rather than optional chaining at ~40 call sites: the chaining
 * has to be right every time, and `fetchSheetSchema` needs a real object rather
 * than a `?.` anyway.
 */
export const NO_OP_LOAD_TRACE: LoadTrace = {
  mark: () => undefined,
  fail: () => undefined,
  warn: () => undefined,
  succeed: () => undefined,
  abandon: () => undefined,
  stepsReached: [],
  failureSummary: null,
};

/**
 * The bookkeeping slot of the conversion-wide trace. Never printed — the code
 * names a sheet instead (see `beginContainerLoad`) — and deliberately not of the
 * `S<role>` form, so it can never collide with a sheet's own slot.
 */
const CONTAINER_TRACE_SLOT = 'container';

/**
 * The slot of the page-entry trace — the ONE stall clock for entering a filing
 * page, from the container opening to the landing sheet finishing. See
 * {@link SheetLoadTraceService.beginEntryLoad}.
 */
const ENTRY_TRACE_SLOT = 'entry';

/** The entry trace's only step: the landing sheet (and the conversion load) finished. */
const ENTRY_TERMINAL_STEP = 1;

/**
 * The scope printed when no sheet can be named: a bare `S`, the same fallback
 * `filingSheetScope` uses for the pre-navigation session gate.
 */
const UNKNOWN_SHEET_SCOPE = 'S';

/**
 * Records which steps of a filing load actually completed, and turns a failure
 * into a toast a reporter can screenshot and a developer can read.
 *
 * ## Why a ledger rather than instrumented catches
 *
 * The obvious implementation is to give each existing `catch` a more specific
 * message. That cannot work here, for two reasons found while tracing the
 * current failure surface:
 *
 * 1. **A catch does not know what ran.** Roles 12 to 19 route the schema fetch,
 *    the JSON parse, the cross-sheet preload, `buildForm`, the edit-pipeline
 *    wiring and `loadSavedState` through a single `.catch`. Whatever it says,
 *    it says for all six. Only a ledger written as work completes can say which
 *    of them was reached.
 *
 * 2. **The worst failure never reaches a catch at all.** Every sheet awaits the
 *    container's MBRS hydration flag before clearing its own loading flag. That
 *    await is on a `BehaviorSubject` that can fail to emit, and a promise that
 *    never settles is not an error — so `ngOnInit` simply stops mid-body and the
 *    sheet spins forever with no thrown error, no failed request and no console
 *    output in production. That is the open, unreproduced defect described under `STALL` in
 *    `docs/development/filing-load-diagnostics.md`,
 *    and it is exactly the incident this work was asked to make diagnosable.
 *    The watchdog below is what gives it a voice: no exception required.
 *
 * ## One trace per scope
 *
 * Traces are keyed by slot — one for the conversion-wide load, one per sheet
 * (`'S8'`, …) — and starting a new one closes any previous trace in that same
 * slot. Sheets remount on every sheet change, so without that the watchdog
 * timers would pile up and a long editing session would eventually fire a
 * stall toast for a sheet the user left ten minutes ago.
 *
 * The slot is NOT the scope printed in the code. Both halves of the load print
 * `S<role>` (see `beginContainerLoad`), so the conversion-wide load keeps a
 * slot of its own, {@link CONTAINER_TRACE_SLOT}; sharing `'S8'` with the SOFP
 * sheet would let one supersede the other.
 *
 * @see src/app/utils/sheet-load-trace.util.ts for the step and failure vocabularies.
 * @see docs/development/filing-load-diagnostics.md for the full contract.
 */
@Injectable({ providedIn: 'root' })
export class SheetLoadTraceService {
  private readonly authFailureNotice = inject(AuthFailureNoticeService);
  // REFACTORED-DIAGNOSTIC-CODES-FLAG: remove with the flag once production is cleared.
  private readonly runtimeConfig = inject(RuntimeConfigService);

  /** Live traces, keyed by scope. At most one per scope; see the class docblock. */
  private readonly activeTraces = new Map<string, InternalTrace>();

  private readonly failuresSubject = new Subject<LoadFailureNotice>();

  /** The open page-entry trace, or null once entry finished/failed/was abandoned. */
  private entryTrace: InternalTrace | null = null;
  /** An attached sheet trace reached its terminal step while the entry was open. */
  private entrySheetFinished = false;

  /** A guard notice raised before the shell subscribed; see {@link reportPreNavigationFailure}. */
  private pendingPreNavigationNotice: LoadFailureNotice | null = null;

  /**
   * Load failures, ready to be rendered as a toast by an always-mounted shell.
   *
   * ## Why this service does not raise the toast itself
   *
   * It cannot, and two separate constraints say so.
   *
   * **It has no `MessageService` to inject.** `MessageService` is
   * component-scoped throughout this app — every filing component declares it in
   * its own `providers` alongside the `<p-toast>` that renders what that
   * instance publishes — and `app.config.ts` provides none. A root singleton
   * injecting one throws `NullInjectorError` on first use.
   *
   * **And the obvious workaround does not survive the redirect.** Taking the
   * caller's instance was tried first and looked correct in every test: the
   * message is added, to the right instance, before anything unmounts. It still
   * showed nothing on the failure that matters most. A transport failure makes
   * `RetrieveMbrsDataService` navigate to the dashboard, which destroys the
   * filing container and takes its `<p-toast>` with it — so the toast was
   * published into an outlet that was being torn down in the same tick. The
   * user was bounced to the dashboard with no explanation, which is the exact
   * complaint the diagnostic code exists to answer.
   *
   * Emitting instead lets `MainLayoutComponent` — mounted for the whole
   * authenticated area, and the parent of both `/mbrs/filing` and
   * `/mbrs/dashboard` — render it. This is the same shape
   * `FilingFormPersistenceService.writeErrors$` already uses, and for the same
   * reason.
   *
   * Only `PERM`, `RESP` and `AUTH` leave the page (see
   * `FilingFormContainerComponent.handleContainerLoadFailure`): `PERM` via
   * `redirectOnPermissionFailure`, `RESP` to the XBRL Files listing via
   * `redirectToConversionListing`, `AUTH` to the login page through
   * `forceLogout` once the session gate confirms it ended — the container
   * never navigates for `AUTH` itself. Every other class stays on the filing
   * page behind the failure panel. Both are plain in-app Angular navigations
   * (`router.navigate()`/`router.navigateByUrl()`).
   * An earlier revision of `PERM`'s redirect used `Location.back()` instead,
   * which could restore the destination page from the browser's
   * back-forward cache instead of navigating at all — no script runs during
   * that restore, so nothing could consume a notice staged for it. That is
   * why `PERM` moved to `router.navigateByUrl()`, which does not have this
   * failure mode, rather than adding more machinery to cope with it.
   */
  readonly failures$: Observable<LoadFailureNotice> =
    this.failuresSubject.asObservable();

  /**
   * Report a failure found BEFORE a filing page loads — the pre-navigation
   * guard's "this conversion does not exist / is not yours". There is no trace
   * to fail (no step ledger, no watchdog: it is one request), so this composes
   * the same toast a trace would and emits it on {@link failures$}.
   *
   * The shell renders it, which is what lets the toast survive whichever
   * outcome the guard chooses: staying on the current page, or redirecting.
   * Production (flag off) shows the previous generic sentence and no code.
   *
   * @param failure       `RESP` (conversion not found) or `PERM` (not yours).
   * @param scope         `S<role>` of the sheet being opened — see `resolveDiagnosticScopeFromUrl`.
   * @param conversionId  The conversion that was refused, for the console record.
   * @param cause         Status / errorCode / errors text, console-only.
   */
  reportPreNavigationFailure(
    failure: Extract<LoadFailureClass, 'RESP' | 'PERM'>,
    scope: string,
    conversionId: string | null,
    cause: unknown,
  ): void {
    const code = formatLoadDiagnosticCode(scope, failure, conversionId);
    const toastCode = formatToastDiagnosticCode(scope, failure, conversionId);
    // Unconditional, including production: the toast omits the cause, so this
    // is the only place a developer can read it. Full code, PERM included.
    console.error(`[filing-guard] ${code}`, { scope, failure, conversionId, cause });
    const notice = composeLoadFailureNotice(
      failure,
      toastCode,
      this.runtimeConfig.refactoredDiagnosticCodesEnabled(),
      { scope, origin: 'conversion', terminal: true },
    );
    // A guard that fires on a COLD LOAD (typed URL, refresh, new tab) decides
    // before any component exists, so the shell has not subscribed to
    // `failures$` yet and a bare emission would go to nobody — the user was
    // redirected with no explanation. Hold the notice until the shell mounts
    // and drains it (`consumePendingPreNavigationNotice`). When the shell IS
    // up (an in-app click), emit as usual. Same shape as
    // `AccessDeniedNoticeService`, for the same reason.
    if (this.failuresSubject.observed) {
      this.failuresSubject.next(notice);
    } else {
      this.pendingPreNavigationNotice = notice;
    }
  }

  /**
   * Hand the shell a guard notice that was raised before it mounted, exactly
   * once. `MainLayoutComponent` calls this from `ngAfterViewInit` — not
   * `ngOnInit` — because the toast outlet subscribes to its message stream in
   * its own `ngOnInit`, which runs after the shell's.
   */
  consumePendingPreNavigationNotice(): LoadFailureNotice | null {
    const notice = this.pendingPreNavigationNotice;
    this.pendingPreNavigationNotice = null;
    return notice;
  }

  /**
   * Open a trace for the conversion-wide half of the load — the
   * `retrieve-mbrs-data` call and the populate/validation work that follows it.
   *
   * @param conversionId       The conversion being opened, or null if not yet known.
   * @param resolveSheetScope  Returns the `S<role>` scope of the sheet the user
   *                           was trying to reach, e.g. `S4` for Directors
   *                           Report. Omit it and the code prints a bare `S`.
   *
   * ## Why the code names a sheet role, not the container (P.O., 2026-10-12)
   *
   * A conversion-wide failure prints the same `S<role>` scope as a sheet's own
   * failure, so `MBRS-S4-PERM` is "Directors Report could not be opened". The
   * toast only ever appears on the filing page, so the sheet number is enough
   * to tell a reader where it came from. The cost, accepted: the code alone no
   * longer says whether the whole conversion or only that sheet failed. The
   * failure class and the console's `steps reached` ledger still do.
   *
   * It replaced `C<position>`, the sheet's index in the dropdown, which is not
   * its role number — Statement of Financial Position was `C9` here and `S8`
   * everywhere else — and disagreed with the session gate's `S<role>` AUTH
   * code for the same sheet.
   *
   * ## Why a function and not a value
   *
   * The scope is resolved when the trace FAILS, not when it opens. The trace
   * opens as soon as IndexedDB hydration settles, before the container has
   * restored the saved sheet or applied `?sheet=` — so a value captured then
   * was the default first sheet on almost every cold load, whichever sheet was
   * asked for.
   */
  /**
   * Open the ONE stall clock for entering a filing page (P.O.: one loader, one
   * timer, one tracker, from start to finish).
   *
   * The preparer sees one continuous loader from the moment the page opens —
   * IndexedDB hydration, the conversion fetch, the landing sheet's schema and
   * form — even though the code runs it as separate stages with separate traces.
   * Giving each stage its own 15s watchdog meant a hang before the sheet mounted
   * was invisible, and a slow-but-live entry could toast one `STALL` per stage
   * (an `S0` twice, then an `S1`), each with a clock that restarted.
   *
   * While this entry is open, the container and sheet traces opened after it
   * record their steps as usual but arm NO watchdog of their own. The entry
   * trace is the only timer. It ends when:
   *  - the landing sheet finishes AND the conversion-wide trace (if any) has
   *    finished — success, clock cancelled;
   *  - any attached trace reports a classified failure — that report stands, the
   *    clock is cancelled so a real cause is never followed by a stall;
   *  - it is abandoned (the user left the page).
   * If the clock fires first, ONE `STALL` is reported and the console record
   * carries every attached ledger, so the missing step is still readable.
   *
   * Traces opened after the entry ends (a later sheet switch) arm their own
   * watchdog exactly as before: that is a new loader with a new clock.
   *
   * @param resolveSheetScope  Resolved when the stall fires; see {@link beginContainerLoad}.
   */
  beginEntryLoad(
    conversionId: string | null,
    resolveSheetScope: () => string = () => UNKNOWN_SHEET_SCOPE,
  ): LoadTrace {
    this.entrySheetFinished = false;
    return this.beginTrace(ENTRY_TRACE_SLOT, resolveSheetScope, ENTRY_TERMINAL_STEP, conversionId);
  }

  beginContainerLoad(
    conversionId: string | null,
    resolveSheetScope: () => string = () => UNKNOWN_SHEET_SCOPE,
  ): LoadTrace {
    return this.beginTrace(
      CONTAINER_TRACE_SLOT,
      resolveSheetScope,
      CONTAINER_TERMINAL_STEP,
      conversionId,
    );
  }

  /**
   * Open a trace for one sheet's mount.
   *
   * @param roleLabel    The role's short label as it should appear in the code,
   *                     e.g. `'8'` or `'15-410000'`. Prefixed with `S`;
   *                     its hyphen prints as `_` (`S15_410000`), see
   *                     `toDiagnosticScopeToken`.
   * @param conversionId The conversion being opened, or null if not yet known.
   * @param destroyRef   The calling component's `DestroyRef`. The trace closes
   *                     itself when that component is destroyed.
   *
   * ## Why the DestroyRef, and why it is required rather than optional
   *
   * A sheet is rendered by `*ngIf="currentSheet === '…'"`, so changing the
   * selected sheet DESTROYS one component and creates another. A cold load
   * reassigns `currentSheet` at least once on its own — `enforcePhaseGatedSheet`
   * and then the landing-sheet resolution — so two different sheets mount
   * during a single load, under two different scopes.
   *
   * Per-scope supersession does not help there: it dedupes a remount of the
   * SAME sheet, and these are different ones. Without this hook the first
   * sheet's watchdog stays armed after it has left the screen, and a stalled
   * load reported itself twice — once per sheet that happened to mount. More
   * generally, a toast naming a sheet the user is no longer looking at is
   * worse than no toast.
   *
   * Required, not optional, because "remember to close your trace" is exactly
   * the kind of step that gets missed on the 27th call site.
   */
  beginSheetLoad(
    roleLabel: string,
    conversionId: string | null,
    destroyRef: DestroyRef,
  ): LoadTrace {
    const scope = `S${roleLabel}`;
    const trace = this.beginTrace(scope, () => scope, SHEET_TERMINAL_STEP, conversionId);
    destroyRef.onDestroy(() => trace.abandon());
    return trace;
  }

  /**
   * Close every live trace without reporting. Called when the user deliberately
   * leaves the filing form: a load abandoned by navigation is not a failure, and
   * must not fire a stall toast onto whatever page they landed on.
   */
  abandonAll(): void {
    for (const trace of [...this.activeTraces.values()]) {
      trace.dispose();
    }
    this.activeTraces.clear();
  }

  private beginTrace(
    slot: string,
    resolveScope: () => string,
    terminalStep: number,
    conversionId: string | null,
  ): LoadTrace {
    // A remount supersedes whatever was in flight in this slot. Disposing
    // rather than failing: the previous attempt was replaced, not broken.
    this.activeTraces.get(slot)?.dispose();

    const isEntry = slot === ENTRY_TRACE_SLOT;
    const trace: InternalTrace = new InternalTrace(
      isEntry ? 'entry' : slot === CONTAINER_TRACE_SLOT ? 'conversion' : 'sheet',
      resolveScope,
      terminalStep,
      conversionId,
      // REFACTORED-DIAGNOSTIC-CODES-FLAG: read at failure time, not at trace creation.
      () => this.runtimeConfig.refactoredDiagnosticCodesEnabled(),
      (notice) => this.failuresSubject.next(notice),
      // Compose the full toast line here, not a bare code: `AuthFailureNoticeService`
      // stages whatever `LoginComponent` shows verbatim, and this is the one
      // class (AUTH) it is actually correct to assume for — see that
      // service's own docblock for why a bare code is no longer enough.
      (code) => this.authFailureNotice.stage(`${LOAD_FAILURE_COPY.AUTH} ${code}`),
      // Identity-checked: a superseded trace closing LATE must not evict the
      // trace that replaced it. Without the check, an unmounting sheet's
      // deferred close would free a slot its successor already owns, and the
      // next mount of that sheet would no longer supersede anything.
      () => {
        if (this.activeTraces.get(slot) === trace) {
          this.activeTraces.delete(slot);
        }
        if (isEntry && this.entryTrace === trace) {
          this.entryTrace = null;
        }
      },
      // Attached to an open entry: the entry's clock is the only timer.
      isEntry || this.entryTrace === null,
      (outcome) => this.onTraceOutcome(slot, outcome),
      isEntry ? () => this.entryLedgers() : undefined,
    );
    this.activeTraces.set(slot, trace);
    if (isEntry) {
      this.entryTrace = trace;
    }
    return trace;
  }

  /**
   * An attached trace closed. The entry follows: a classified failure stands on
   * its own and cancels the clock; a success settles the entry once both the
   * landing sheet and the conversion-wide load are done.
   */
  private onTraceOutcome(slot: string, outcome: 'succeeded' | 'failed'): void {
    const entry = this.entryTrace;
    if (entry === null || slot === ENTRY_TRACE_SLOT) return;
    if (outcome === 'failed') {
      entry.dispose();
      return;
    }
    if (slot !== CONTAINER_TRACE_SLOT) {
      this.entrySheetFinished = true;
    }
    if (this.entrySheetFinished && !this.activeTraces.has(CONTAINER_TRACE_SLOT)) {
      entry.mark(ENTRY_TERMINAL_STEP);
    }
  }

  /** The step ledgers of every still-open attached trace, for the stall's console record. */
  private entryLedgers(): Record<string, readonly number[]> {
    const ledgers: Record<string, readonly number[]> = {};
    for (const [slot, trace] of this.activeTraces) {
      if (slot !== ENTRY_TRACE_SLOT) ledgers[slot] = trace.stepsReached;
    }
    return ledgers;
  }
}

/**
 * The concrete trace. Kept private to this file so the only way to obtain one is
 * through the service, which owns the per-slot bookkeeping.
 */
class InternalTrace implements LoadTrace {
  private readonly steps = new Set<number>();
  private readonly startedAt = Date.now();
  private watchdog: ReturnType<typeof setTimeout> | null = null;
  private closed = false;
  failureSummary: string | null = null;

  constructor(
    /**
     * Which half of the load this is. Console-only: the toast code prints
     * `S<role>` for both, so this is where a developer can still tell them apart.
     */
    private readonly origin: 'entry' | 'conversion' | 'sheet',
    /** Resolved at failure time — see `SheetLoadTraceService.beginContainerLoad`. */
    private readonly resolveScope: () => string,
    private readonly terminalStep: number,
    private readonly conversionId: string | null,
    /** REFACTORED-DIAGNOSTIC-CODES-FLAG: `false` swaps the coded toast for the legacy generic one. */
    private readonly diagnosticCodesEnabled: () => boolean,
    private readonly report: (notice: LoadFailureNotice) => void,
    private readonly stageAuthFailure: (code: string) => void,
    private readonly onClose: () => void,
    /** False when an open page-entry trace owns the stall clock instead. */
    armWatchdog = true,
    private readonly onOutcome: (outcome: 'succeeded' | 'failed') => void = () => undefined,
    /** Entry only: other traces' ledgers, appended to the stall's console record. */
    private readonly collectLedgers?: () => Record<string, readonly number[]>,
  ) {
    if (armWatchdog) {
      this.watchdog = setTimeout(() => this.fail('STALL'), LOAD_WATCHDOG_MS);
    }
  }

  get stepsReached(): readonly number[] {
    return [...this.steps].sort((left, right) => left - right);
  }

  mark(step: number): void {
    if (this.closed) return;
    this.steps.add(step);
    // Reaching the terminal step IS success — no caller has to remember to say
    // so, which keeps the 26 sheets' finalize paths to a single added line.
    if (step === this.terminalStep) {
      this.succeed();
    }
  }

  succeed(): void {
    if (this.closed) return;
    this.closed = true;
    this.dispose();
    this.onOutcome('succeeded');
  }

  abandon(): void {
    // Same effect as succeed() today, but they are NOT synonyms and must stay
    // separate: succeed() means the load reached its terminal step, abandon()
    // means nobody is waiting for it any more. If either ever grows a side
    // effect — a metric, a log line — it will need only one of them.
    this.dispose();
  }

  warn(failure: LoadFailureClass, cause?: unknown): void {
    // A closed trace has already reported (or been abandoned); a silent class
    // (AUTH) is never a degraded outcome. Neither may toast from here.
    if (this.closed || SILENT_FAILURE_CLASSES.has(failure)) return;

    const scope = this.resolveScope();
    const code = formatLoadDiagnosticCode(scope, failure, this.conversionId);
    const toastCode = formatToastDiagnosticCode(scope, failure, this.conversionId);

    // Console-only detail, like `fail()` — unconditional, so production keeps it.
    console.warn(
      `[filing-load] ${code} — degraded, load continues` +
        ` — steps reached: ${formatStepsReachedForConsole(this.stepsReached)}`,
      { scope, origin: this.origin, stepsReached: this.stepsReached, failure, conversionId: this.conversionId, cause },
    );

    // Deliberately NOT closing the trace, NOT touching the watchdog and NOT setting
    // `failureSummary` — see `LoadTrace.warn`.
    this.report(
      composeLoadFailureNotice(failure, toastCode, this.diagnosticCodesEnabled(), {
        scope,
        origin: this.origin,
        terminal: false,
      }),
    );
  }

  fail(failure: LoadFailureClass, cause?: unknown): void {
    if (this.closed) return;
    this.closed = true;

    const scope = this.resolveScope();
    const code = formatLoadDiagnosticCode(scope, failure, this.conversionId);
    // What the USER sees. Differs from `code` only for an identifier-free class
    // (PERM: `MBRS-PERM`) — see IDENTIFIER_FREE_FAILURE_CLASSES. The console
    // line above keeps the full code so the report stays actionable.
    const toastCode = formatToastDiagnosticCode(scope, failure, this.conversionId);

    // Logged unconditionally, including in production. The toast carries only
    // the classification a reporter can screenshot; the step ledger — which
    // steps completed, and so which one did not — is deliberately console-only
    // now, printed inline here rather than left for a reader to dig out of the
    // metadata object below.
    console.error(
      `[filing-load] ${code} after ${Math.round((Date.now() - this.startedAt) / 1000)}s` +
        ` — steps reached: ${formatStepsReachedForConsole(this.stepsReached)}`,
      {
        scope,
        origin: this.origin,
        stepsReached: this.stepsReached,
        failure,
        conversionId: this.conversionId,
        cause,
        attachedLedgers: this.collectLedgers?.(),
      },
    );

    // Logged above unconditionally. See SILENT_FAILURE_CLASSES — today that is
    // AUTH alone, which must not toast HERE (the 401 has already sent the user
    // to the login page, which has already navigated away from this one by the
    // time anything could render). Stage the code instead, so the login page
    // can show it once it lands.
    if (SILENT_FAILURE_CLASSES.has(failure)) {
      this.stageAuthFailure(toastCode);
      this.dispose();
      this.onOutcome('failed');
      return;
    }

    // REFACTORED-DIAGNOSTIC-CODES-FLAG: with the flag off (production) the user
    // reads the previous generic sentence, no code. The console record above is
    // unchanged, so support can still ask for a DevTools screenshot. AUTH never
    // reaches here (silent), so the legacy lookup always has a message.
    const notice = composeLoadFailureNotice(failure, toastCode, this.diagnosticCodesEnabled(), {
      scope,
      origin: this.origin,
      terminal: true,
    });

    this.failureSummary = notice.summary;

    // Both container-scope and sheet-scope failures report live here.
    // `FilingFormContainerComponent.handleContainerLoadFailure` decides what
    // happens next: PERM and RESP navigate, AUTH leaves for login via `forceLogout`,
    // everything else stays on the page. Any navigation is a plain in-app one —
    // see `failures$`'s own docblock for why
    // an earlier `Location.back()`-based design needed (and has since shed)
    // extra machinery here.
    this.report(notice);

    this.dispose();
    this.onOutcome('failed');
  }

  /** Cancel the watchdog and release the service's slot. Idempotent. */
  dispose(): void {
    this.closed = true;
    if (this.watchdog !== null) {
      clearTimeout(this.watchdog);
      this.watchdog = null;
    }
    this.onClose();
  }
}
